import { usePageTitle } from "@/composables/shared/usePageTitle";
import { useAuthStore } from "@/stores/authStore";
import type { RouteLocationNormalized, RouteLocationRaw, Router } from "vue-router";
import { EHTTPError, getErrorContent } from "@/router/errors";
import { setupOverlayBackGuard } from "@/router/overlayBackGuard";
import { NO_LANDING_ROUTE, useLandingRoute } from "@/composables/shared/useLandingRoute";

/**
 * Where to send a user who failed a privilege check.
 *
 * Only SECTION-level denials redirect: the role has no business in that part of the
 * app at all (staff without `READ patient.patient` and the whole patient area), so
 * an error page there is a dead end. An ACTION-level denial — can't CREATE a patient,
 * can't CREATE ortho needs — must still render the 401: the user is already in the
 * right section, and bouncing them to its dashboard would silently swallow the
 * refusal instead of telling them what was denied.
 *
 * Routes opt in via `meta.privilegeScope: "section"`; anything unmarked is treated as
 * action-level, so the error page stays the default and only deliberate whole-section
 * gates redirect.
 *
 * Returns `undefined` whenever the Unauthorized page is the right answer. MUST stay
 * synchronous — see the injection-context note below.
 */
function resolvePrivilegeFallback(to: RouteLocationNormalized): RouteLocationRaw | undefined {
  if (to.meta.privilegeScope !== "section") return undefined;

  let target: RouteLocationRaw;
  try {
    target = useLandingRoute().landingRoute.value;
  } catch {
    // Resolving the landing route needs the injected ability. If it isn't there
    // (guard running outside the app context), fall through to the error page
    // rather than letting the throw abort navigation altogether.
    return undefined;
  }

  if (target === NO_LANDING_ROUTE) return undefined;
  if (typeof target === "string" && target === to.path) return undefined;
  if (typeof target === "object" && "name" in target && target.name === to.name) {
    return undefined;
  }

  return target;
}

/**
 * Blocks privilege checks until the session has been revalidated against /me.
 *
 * The bootstrap can't simply delay the first navigation: installing the router
 * is what starts it, and the auth store can't be created before that (its setup
 * injects useRouter/useRoute). Nor can the guard `await` the revalidation —
 * that drops Vue Router's injection context, which `checkPrivileges` needs.
 *
 * So the guard re-dispatches instead of suspending: while revalidation is
 * pending it sends the same navigation round again (synchronously, context
 * intact). Once `openSessionGate()` fires, the retry proceeds normally against
 * a freshly rebuilt ability.
 */
let sessionGateOpen = true;
let onSessionGateOpen: (() => void) | null = null;

export const closeSessionGate = () => {
  sessionGateOpen = false;
};

export const openSessionGate = () => {
  sessionGateOpen = true;
  onSessionGateOpen?.();
  onSessionGateOpen = null;
};

export function setupRouterGuards(router: Router, i18n: any) {
  setupOverlayBackGuard(router);

  router.beforeEach(async (to, _from, next) => {
    // Hold the very first navigation until /me has landed, without awaiting
    // here (an await would drop the injection context `checkPrivileges` needs).
    if (!sessionGateOpen) {
      onSessionGateOpen = () => router.replace(to.fullPath);
      return next(false);
    }

    const authStore = useAuthStore();

    if (
      (to.path.includes("/auth/login") ||
        to.path.startsWith("/sso/") ||
        to.path.startsWith("/auth/sso/")) &&
      authStore.isSessionReady
    ) {
      return next({ path: "/" });
    }

    if (to.meta.requiresAuth && !authStore.isLoggedIn) {
      return next({
        path: "/auth/login",
        query: { redirect: to.fullPath },
      });
    }

    const errorAction = to.path === "/" ? "logout" : "home";

    if (to.path === "/unauthorized") {
      to.meta = {
        ...to.meta,
        ...getErrorContent(EHTTPError.UNAUTHORIZED, errorAction),
      };
    } else if (to.matched.length === 0) {
      to.meta = {
        ...to.meta,
        ...getErrorContent(EHTTPError.NOT_FOUND, errorAction),
      };
    }

    if (to.meta.checkPrivileges && typeof to.meta.checkPrivileges === "function") {
      // Re-apply the persisted ability before evaluating privileges, so a guard
      // that runs before the ability singleton is populated doesn't wrongly
      // render the Unauthorized page.
      //
      // This MUST stay synchronous. Vue Router runs guards inside the app's
      // injection context, but only synchronously — checkPrivileges calls
      // useAbility() (inject), so an `await` here would drop the context and
      // throw "Cannot inject Ability instance because it was not provided".
      if (authStore.isLoggedIn) {
        authStore.restoreAbilityRules();
      }
      const allowed = to.meta.checkPrivileges(to);
      if (!allowed) {
        // A role may legitimately lack a whole section (e.g. staff without
        // `READ patient.patient`). Sending them to a section they can reach beats
        // an Unauthorized page whose only escape is a route they also can't open.
        const fallback = resolvePrivilegeFallback(to);
        if (fallback) {
          return next(fallback);
        }

        to.meta = {
          ...to.meta,
          ...getErrorContent(EHTTPError.UNAUTHORIZED, errorAction),
        };
      }
    }

    next();
  });

  router.afterEach(async () => {
    const { clearPageEntity, updateTitle } = usePageTitle({ i18n: i18n.global });
    clearPageEntity();
    updateTitle();
  });

  return router;
}
